'use strict';

/**
 * Accessible custom dropdown that progressively enhances a native <select>.
 * The native element stays in the DOM as the source of truth (value, form
 * submission, `change` events) so existing handlers, GA tracking and
 * validation keep working. Follows the WAI-ARIA select-only combobox pattern.
 *
 * Opt-in: add the `js-custom-select` class to any `<select>` to enhance it.
 */

var initialized = false;
var uid = 0;

// Skip multi-selects, customized PDP variation selects, and any opt-out.
var EXCLUDE_SELECTOR = '[multiple], .js-variation-select, .js-select-native';

// Account/registration/login pages share one of these body classes/wrapper.
var ACCOUNT_PAGE = /(^|\s)(pt_account|pt_register|pt_sing|pt_tradecard)(\s|$)/;

/**
 * Account pages enhance native form selects automatically; elsewhere it is
 * opt-in via the `js-custom-select` class.
 *
 * @returns {boolean} true on account/registration/login pages
 */
function isAccountContext() {
    return ACCOUNT_PAGE.test(document.body.className) || !!document.querySelector('.tau-unique-account');
}

/**
 * @returns {string} selector for the selects to enhance on the current page
 */
function getSelector() {
    return isAccountContext()
        ? 'select.js-custom-select, select.input-select'
        : 'select.js-custom-select';
}

var CSS = {
    ROOT: 'c-select',
    OPEN: 'is-open',
    DROP_UP: 'is-drop-up',
    DISABLED: 'is-disabled',
    NATIVE: 'c-select_native',
    TOGGLE: 'c-select_toggle',
    VALUE: 'c-select_value',
    LIST: 'c-select_list',
    OPTION: 'c-select_option',
    OPTION_ACTIVE: 'is-active',
    OPTION_SELECTED: 'is-selected',
    ENHANCED: 'js-custom-select_enhanced'
};

var KEY = {
    ENTER: 'Enter',
    SPACE: ' ',
    SPACE_LEGACY: 'Spacebar',
    ESC: 'Escape',
    ESC_LEGACY: 'Esc',
    UP: 'ArrowUp',
    UP_LEGACY: 'Up',
    DOWN: 'ArrowDown',
    DOWN_LEGACY: 'Down',
    HOME: 'Home',
    END: 'End',
    TAB: 'Tab'
};

/** Currently open instance (only one open at a time). */
var openInstance = null;

/**
 * Builds the custom dropdown markup for a single native select.
 *
 * @param {HTMLSelectElement} selectEl native select element
 * @returns {void}
 */
function CustomSelect(selectEl) {
    this.$native = $(selectEl);
    this.id = 'custom-select-' + (uid += 1);
    this.listId = this.id + '_list';
    this.valueId = this.id + '_value';
    this.typeahead = '';
    this.typeaheadTimer = null;
    this.activeIndex = -1;
    this.internalChange = false;

    this.build();
    this.bind();
    this.syncFromNative();
}

CustomSelect.prototype.build = function () {
    var label = this.$native.attr('aria-label') || '';

    this.$root = $('<div>', {
        'class': CSS.ROOT,
        'id': this.id
    });

    this.$toggle = $('<div>', {
        'class': CSS.TOGGLE,
        'role': 'combobox',
        'tabindex': this.$native.prop('disabled') ? -1 : 0,
        'aria-haspopup': 'listbox',
        'aria-expanded': 'false',
        'aria-controls': this.listId
    });

    this.$value = $('<span>', {
        'class': CSS.VALUE,
        'id': this.valueId
    });
    this.$toggle.append(this.$value);

    this.$list = $('<ul>', {
        'class': CSS.LIST,
        'id': this.listId,
        'role': 'listbox',
        'tabindex': -1
    });

    this.applyLabel(label);
    this.buildOptions();

    // Keep native select in DOM but hidden from AT and tab order.
    this.$native
        .addClass(CSS.NATIVE)
        .attr({ 'aria-hidden': 'true', 'tabindex': -1 });

    this.$root.append(this.$toggle).append(this.$list);
    this.$native.after(this.$root);
    this.$native.appendTo(this.$root);
    this.$root.addClass(CSS.ENHANCED);

    if (this.$native.prop('disabled')) {
        this.$root.addClass(CSS.DISABLED);
    }
};

/** Gives the toggle/list an accessible name from aria-label or the field's label. */
CustomSelect.prototype.applyLabel = function (label) {
    if (label) {
        this.$toggle.attr('aria-label', label);
        this.$list.attr('aria-label', label);
        return;
    }

    var nativeId = this.$native.attr('id');
    if (!nativeId) {
        return;
    }

    var $label = $('label[for="' + nativeId + '"]').first();
    if (!$label.length) {
        return;
    }

    if (!$label.attr('id')) {
        $label.attr('id', this.id + '_label');
    }
    this.$toggle.attr('aria-labelledby', $label.attr('id') + ' ' + this.valueId);
    this.$list.attr('aria-labelledby', $label.attr('id'));
};

/** (Re)builds the option list from the native select's current options. */
CustomSelect.prototype.buildOptions = function () {
    var self = this;
    this.options = [];
    this.$list.empty();

    this.$native.children('option').each(function (index) {
        var $option = $(this);
        var optionId = self.listId + '_opt' + index;
        var $item = $('<li>', {
            'class': CSS.OPTION,
            'id': optionId,
            'role': 'option',
            'html': $option.html()
        }).attr('data-value', $option.attr('value'));

        self.options.push({
            $item: $item,
            value: $option.attr('value'),
            label: ($option.text() || '').trim().toLowerCase(),
            disabled: $option.prop('disabled')
        });

        if ($option.prop('disabled')) {
            $item.attr('aria-disabled', 'true');
        }

        self.$list.append($item);
    });
};

/** Rebuilds options and re-syncs when the native options change externally. */
CustomSelect.prototype.rebuild = function () {
    this.close();
    this.buildOptions();
    this.syncFromNative();
};

CustomSelect.prototype.bind = function () {
    var self = this;

    this.$toggle.on('click', function () {
        if (self.$native.prop('disabled')) {
            return;
        }
        self.toggle();
    });

    this.$toggle.on('keydown', function (event) {
        self.onToggleKeydown(event);
    });

    this.$list.on('click', '.' + CSS.OPTION, function () {
        var index = self.$list.children().index(this);
        self.selectByIndex(index, true);
    });

    this.$list.on('mousemove', '.' + CSS.OPTION, function () {
        var index = self.$list.children().index(this);
        self.setActive(index);
    });

    // Reflect external value changes (prefill / programmatic updates).
    this.$native.on('change', function () {
        if (!self.internalChange) {
            self.syncFromNative();
        }
    });

    // Rebuild when options are replaced (dependent / dynamic selects).
    if (window.MutationObserver) {
        self.observer = new window.MutationObserver(function () {
            self.rebuild();
        });
        self.observer.observe(self.$native.get(0), { childList: true });
    }
};

/** Reflects the native select's current value in the custom UI. */
CustomSelect.prototype.syncFromNative = function () {
    var selectedIndex = this.$native.prop('selectedIndex');
    if (selectedIndex < 0) {
        selectedIndex = 0;
    }
    this.setSelected(selectedIndex, false);
};

CustomSelect.prototype.setSelected = function (index, notify) {
    var option = this.options[index];
    if (!option) {
        return;
    }

    this.selectedIndex = index;
    this.$value.html(option.$item.html());

    this.options.forEach(function (opt, i) {
        var isSelected = i === index;
        opt.$item.toggleClass(CSS.OPTION_SELECTED, isSelected);
        opt.$item.attr('aria-selected', isSelected ? 'true' : 'false');
    });

    if (this.$native.prop('selectedIndex') !== index) {
        this.$native.prop('selectedIndex', index);
        if (notify) {
            this.fireChange();
        }
    }
};

// Fires the native change; guarded so a throwing consumer handler cannot break
// selection/close, and the internalChange flag is always reset.
CustomSelect.prototype.fireChange = function () {
    this.internalChange = true;
    try {
        this.$native.trigger('change');
    } catch (error) {
        this.internalChange = false;
        throw error;
    }
    this.internalChange = false;
};

CustomSelect.prototype.setActive = function (index) {
    var option = this.options[index];
    if (!this.isSelectable(index)) {
        return;
    }

    this.activeIndex = index;
    this.options.forEach(function (opt, i) {
        opt.$item.toggleClass(CSS.OPTION_ACTIVE, i === index);
    });
    this.$toggle.attr('aria-activedescendant', option.$item.attr('id'));

    var listEl = this.$list.get(0);
    var itemEl = option.$item.get(0);
    if (listEl && itemEl) {
        if (itemEl.offsetTop < listEl.scrollTop) {
            listEl.scrollTop = itemEl.offsetTop;
        } else if (itemEl.offsetTop + itemEl.offsetHeight > listEl.scrollTop + listEl.clientHeight) {
            listEl.scrollTop = itemEl.offsetTop + itemEl.offsetHeight - listEl.clientHeight;
        }
    }
};

CustomSelect.prototype.selectByIndex = function (index, notify) {
    var option = this.options[index];
    if (!option || option.disabled) {
        return;
    }
    // Close before firing change so a throwing consumer handler cannot leave
    // the list open (e.g. role selector on trade registration).
    this.setSelected(index, false);
    this.close();
    this.$toggle.focus();
    if (notify) {
        this.fireChange();
    }
};

CustomSelect.prototype.open = function () {
    if (openInstance && openInstance !== this) {
        openInstance.close();
    }
    openInstance = this;

    this.$root.addClass(CSS.OPEN);
    this.$toggle.attr('aria-expanded', 'true');
    this.positionList();
    this.setActive(this.firstSelectableIndex());
};

/**
 * Flips the list above the toggle when there is not enough room below, and
 * caps its height to the available space so it never overflows the viewport.
 */
CustomSelect.prototype.positionList = function () {
    var toggleEl = this.$toggle.get(0);
    var listEl = this.$list.get(0);
    if (!toggleEl || !listEl) {
        return;
    }

    this.$root.removeClass(CSS.DROP_UP);
    listEl.style.maxHeight = '';

    var rect = toggleEl.getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    var edgeGap = 16;
    var spaceBelow = viewportHeight - rect.bottom - edgeGap;
    var spaceAbove = rect.top - edgeGap;
    var naturalHeight = listEl.scrollHeight;

    var dropUp = spaceBelow < naturalHeight && spaceAbove > spaceBelow;
    this.$root.toggleClass(CSS.DROP_UP, dropUp);

    var available = dropUp ? spaceAbove : spaceBelow;
    listEl.style.maxHeight = Math.max(available, 0) + 'px';
};

CustomSelect.prototype.close = function () {
    if (!this.$root.hasClass(CSS.OPEN)) {
        return;
    }
    this.$root.removeClass(CSS.OPEN).removeClass(CSS.DROP_UP);
    this.$list.get(0).style.maxHeight = '';
    this.$toggle.attr('aria-expanded', 'false');
    this.$toggle.removeAttr('aria-activedescendant');
    this.activeIndex = -1;
    if (openInstance === this) {
        openInstance = null;
    }
};

CustomSelect.prototype.toggle = function () {
    if (this.$root.hasClass(CSS.OPEN)) {
        this.close();
    } else {
        this.open();
    }
};

CustomSelect.prototype.isSelectable = function (index) {
    var option = this.options[index];
    return !!option && !option.disabled && index !== this.selectedIndex;
};

CustomSelect.prototype.firstSelectableIndex = function () {
    for (var i = 0; i < this.options.length; i += 1) {
        if (this.isSelectable(i)) {
            return i;
        }
    }
    return -1;
};

CustomSelect.prototype.moveActive = function (step) {
    var index = this.activeIndex;
    var count = this.options.length;
    for (var i = 0; i < count; i += 1) {
        index += step;
        if (index < 0 || index > count - 1) {
            return;
        }
        if (this.isSelectable(index)) {
            this.setActive(index);
            return;
        }
    }
};

CustomSelect.prototype.onToggleKeydown = function (event) {
    var key = event.key;
    var isOpen = this.$root.hasClass(CSS.OPEN);

    switch (key) {
        case KEY.DOWN:
        case KEY.DOWN_LEGACY:
            event.preventDefault();
            if (!isOpen) {
                this.open();
            } else {
                this.moveActive(1);
            }
            break;
        case KEY.UP:
        case KEY.UP_LEGACY:
            event.preventDefault();
            if (!isOpen) {
                this.open();
            } else {
                this.moveActive(-1);
            }
            break;
        case KEY.HOME:
            if (isOpen) {
                event.preventDefault();
                this.setActive(this.firstSelectableIndex());
            }
            break;
        case KEY.END:
            if (isOpen) {
                event.preventDefault();
                for (var i = this.options.length - 1; i >= 0; i -= 1) {
                    if (this.isSelectable(i)) {
                        this.setActive(i);
                        break;
                    }
                }
            }
            break;
        case KEY.ENTER:
        case KEY.SPACE:
        case KEY.SPACE_LEGACY:
            event.preventDefault();
            if (!isOpen) {
                this.open();
            } else if (this.activeIndex >= 0) {
                this.selectByIndex(this.activeIndex, true);
            }
            break;
        case KEY.ESC:
        case KEY.ESC_LEGACY:
            if (isOpen) {
                event.preventDefault();
                this.close();
            }
            break;
        case KEY.TAB:
            if (isOpen) {
                if (this.activeIndex >= 0) {
                    this.setSelected(this.activeIndex, true);
                }
                this.close();
            }
            break;
        default:
            this.onTypeahead(event);
            break;
    }
};

CustomSelect.prototype.onTypeahead = function (event) {
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) {
        return;
    }

    var self = this;
    this.typeahead += event.key.toLowerCase();
    window.clearTimeout(this.typeaheadTimer);
    this.typeaheadTimer = window.setTimeout(function () {
        self.typeahead = '';
    }, 500);

    for (var i = 0; i < this.options.length; i += 1) {
        var option = this.options[i];
        if (!option.disabled && option.label.indexOf(this.typeahead) === 0) {
            if (this.$root.hasClass(CSS.OPEN)) {
                this.setActive(i);
            } else {
                this.setSelected(i, true);
            }
            break;
        }
    }
};

/**
 * Enhances every matching select on the page.
 *
 * @returns {void}
 */
function enhanceAll() {
    $(getSelector()).not(EXCLUDE_SELECTOR).each(function () {
        if ($(this).parent().hasClass(CSS.ROOT)) {
            return;
        }
        // eslint-disable-next-line no-new
        new CustomSelect(this);
    });
}

function initGlobalEvents() {
    $(document).on('click', function (event) {
        if (openInstance && !$(event.target).closest('.' + CSS.ROOT).is(openInstance.$root)) {
            openInstance.close();
        }
    });

    $(document).on('focusin', function (event) {
        if (openInstance && !$(event.target).closest('.' + CSS.ROOT).is(openInstance.$root)) {
            openInstance.close();
        }
    });

    // Auto-close on any viewport scroll/resize (standard desktop floating UI),
    // matching native selects; ignore scrolling inside the open list itself.
    var closeOnViewportChange = function (event) {
        if (!openInstance) {
            return;
        }
        if (event && event.target && $(event.target).closest('.' + CSS.LIST).length) {
            return;
        }
        openInstance.close();
    };

    $(window).on('resize', closeOnViewportChange);
    window.addEventListener('scroll', closeOnViewportChange, true);

    // Enhance selects added later (ajax forms / dialogs) on account pages.
    if (isAccountContext() && window.MutationObserver) {
        var reenhanceTimer;
        var observer = new window.MutationObserver(function () {
            window.clearTimeout(reenhanceTimer);
            reenhanceTimer = window.setTimeout(enhanceAll, 100);
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }
}

module.exports = {
    'init': function () {
        if (initialized) {
            return;
        }

        enhanceAll();
        initGlobalEvents();
        initialized = true;
    }
};
